import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment/moment";
import { Row, Col, Input, FormGroup, HelpBlock, ControlLabel, Alert } from "components/bootstrap";
import {
  MultiSelect,
  Select,
  Spinner,
  TimeUnitInput,
  SearchFiltersFormControls,
  TimezoneSelect
} from "components/common";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import { TIME_UNITS } from "components/event-definitions/event-definition-types/FilterForm";
import useUserDateTime from "hooks/useUserDateTime";
import * as FormsUtils from "util/FormsUtils";
import { describeExpression } from "util/CronUtils";
import { useGetStreams } from "security-app/hooks/useSigmaAPI";
import { ANOMALY_SUBTYPES, getSubtypeByType } from "./anomalySubtypes";
const AnomalyEventForm = ({ eventDefinition, onChange, validation }) => {
  var _a, _b, _c, _d, _e, _f, _g;
  const [currentConfig, setCurrentConfig] = useState(() => ({
    ...eventDefinition.config
  }));
  const selectedSubtype = ((_a = currentConfig.config) == null ? void 0 : _a.type) ? getSubtypeByType(currentConfig.config.type) : void 0;
  const { execute_every_ms: executeEveryMs, cron_expression: cronExpression, window_delay_ms: windowDelayMs } = currentConfig;
  const executeEvery = extractDurationAndUnit(executeEveryMs, TIME_UNITS);
  const [executeEveryMsDuration, setExecuteEveryMsDuration] = useState(executeEvery.duration);
  const [executeEveryMsUnit, setExecuteEveryMsUnit] = useState(executeEvery.unit);
  const windowDelay = extractDurationAndUnit(windowDelayMs, TIME_UNITS);
  const [windowDelayMsDuration, setWindowDelayMsDuration] = useState(windowDelay.duration);
  const [windowDelayMsUnit, setWindowDelayMsUnit] = useState(windowDelay.unit);
  const { userTimezone } = useUserDateTime();
  const [cronDescription, setCronDescription] = useState(
    cronExpression ? describeExpression(cronExpression) : ""
  );
  const { streams, streamCategories, loadingStreams } = useGetStreams();
  const streamOptions = useMemo(
    () => streams.map((stream) => ({ value: stream.id, label: stream.title })),
    [streams]
  );
  const streamCategoryOptions = useMemo(
    () => (streamCategories != null ? streamCategories : []).map((category) => ({ value: category, label: category })),
    [streamCategories]
  );
  const isIlluminate = eventDefinition._scope === "ILLUMINATE";
  const subtypeOptions = useMemo(() => {
    const availableSubtypes = ANOMALY_SUBTYPES.filter((subtype) => {
      if (!subtype.useCondition) return true;
      return subtype.useCondition(eventDefinition);
    });
    return availableSubtypes.map((subtype) => ({
      value: subtype.type,
      label: subtype.displayName
    }));
  }, [eventDefinition]);
  const setCurrentConfigAndPropagateChange = useCallback(
    (config) => {
      setCurrentConfig(config);
      onChange("config", config);
    },
    [onChange]
  );
  const handleSubtypeChange = (nextValue) => {
    var _a2;
    if (!nextValue) return;
    const subtype = getSubtypeByType(nextValue);
    if (!subtype) return;
    const config = cloneDeep(eventDefinition.config);
    config.config = subtype.defaultConfig;
    if (subtype.parentConfigOverrides) {
      Object.assign(config, subtype.parentConfigOverrides);
    }
    if (((_a2 = subtype.parentConfigOverrides) == null ? void 0 : _a2.execute_every_ms) !== void 0) {
      const newEvery = extractDurationAndUnit(subtype.parentConfigOverrides.execute_every_ms, TIME_UNITS);
      setExecuteEveryMsDuration(newEvery.duration);
      setExecuteEveryMsUnit(newEvery.unit);
    }
    setCurrentConfigAndPropagateChange(config);
  };
  const handleConfigChange = (event) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    const config = cloneDeep(eventDefinition.config);
    config[name] = value;
    setCurrentConfigAndPropagateChange(config);
  };
  const handleStreamsChange = (selectedStreams) => {
    const config = cloneDeep(eventDefinition.config);
    config.streams = selectedStreams;
    setCurrentConfigAndPropagateChange(config);
  };
  const handleSearchFiltersChange = useCallback(
    (searchFilters) => {
      const config = cloneDeep(eventDefinition.config);
      config.filters = searchFilters.toArray();
      setCurrentConfigAndPropagateChange(config);
    },
    [eventDefinition.config, setCurrentConfigAndPropagateChange]
  );
  const handleStreamCategoriesChange = useCallback(
    (selected) => {
      const config = cloneDeep(eventDefinition.config);
      config.stream_categories = selected === "" ? [] : selected.split(",");
      setCurrentConfigAndPropagateChange(config);
    },
    [eventDefinition.config, setCurrentConfigAndPropagateChange]
  );
  const handleExecuteEveryChange = (nextValue, nextUnit) => {
    const durationInMs = moment.duration(Math.max(nextValue, 1), nextUnit).asMilliseconds();
    const config = cloneDeep(eventDefinition.config);
    config.execute_every_ms = durationInMs;
    setExecuteEveryMsDuration(nextValue);
    setExecuteEveryMsUnit(nextUnit);
    setCurrentConfigAndPropagateChange(config);
  };
  const handleWindowDelayChange = (nextValue, nextUnit) => {
    const durationInMs = moment.duration(Math.max(nextValue, 0), nextUnit).asMilliseconds();
    const config = cloneDeep(eventDefinition.config);
    config.window_delay_ms = durationInMs;
    setWindowDelayMsDuration(nextValue);
    setWindowDelayMsUnit(nextUnit);
    setCurrentConfigAndPropagateChange(config);
  };
  const handleCronTimezoneChange = (tz) => {
    const config = cloneDeep(eventDefinition.config);
    config.cron_timezone = tz;
    setCurrentConfigAndPropagateChange(config);
  };
  const handleUseCronSchedulingChange = (event) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    const config = cloneDeep(eventDefinition.config);
    config[name] = value;
    if (value) {
      config.cron_expression = "";
      config.cron_timezone = userTimezone;
    } else {
      config.cron_expression = null;
      config.cron_timezone = null;
    }
    setCurrentConfigAndPropagateChange(config);
  };
  const SubtypeFormComponent = selectedSubtype == null ? void 0 : selectedSubtype.formComponent;
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 7, lg: 6 }, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "anomaly-event-subtype" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Detection Type"), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "anomaly-event-subtype-select",
      name: "subtype",
      placeholder: "Select a detection type",
      clearable: false,
      menuPlacement: "bottom",
      value: (_b = currentConfig.config) == null ? void 0 : _b.type,
      options: subtypeOptions,
      onChange: handleSubtypeChange,
      disabled: isIlluminate
    }
  ), selectedSubtype && /* @__PURE__ */ React.createElement(HelpBlock, null, selectedSubtype.description)), !selectedSubtype && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, "Please select a detection method to configure this event definition."), SubtypeFormComponent && /* @__PURE__ */ React.createElement(
    SubtypeFormComponent,
    {
      eventDefinition,
      onChange,
      validation,
      section: "query"
    }
  ), selectedSubtype && /* @__PURE__ */ React.createElement(FormGroup, { controlId: "search-filters" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Search Filters ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")), /* @__PURE__ */ React.createElement("div", { style: { margin: "16px 0" } }, /* @__PURE__ */ React.createElement(SearchFiltersFormControls, { filters: currentConfig.filters, onChange: handleSearchFiltersChange }))), selectedSubtype && !isIlluminate && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "anomaly-event-streams" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Streams"), loadingStreams ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading streams" }) : /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "anomaly-event-streams-select",
      name: "streams",
      placeholder: "Select streams",
      multi: true,
      menuPlacement: "bottom",
      maxMenuHeight: 200,
      value: currentConfig.streams && currentConfig.streams.length > 0 ? currentConfig.streams.join(",") : null,
      options: streamOptions,
      onChange: (newValue) => handleStreamsChange(newValue ? newValue.split(",").filter((value) => value !== "") : [])
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, "Select streams that will be searched for anomalies.")), streamCategoryOptions.length > 0 && /* @__PURE__ */ React.createElement(FormGroup, { controlId: "anomaly-stream-categories" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Stream Categories ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")), /* @__PURE__ */ React.createElement(
    MultiSelect,
    {
      id: "anomaly-stream-categories",
      onChange: handleStreamCategoriesChange,
      options: streamCategoryOptions,
      value: ((_c = currentConfig.stream_categories) != null ? _c : []).join(",")
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, "Select stream categories the search should include.")), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "anomaly-is-cron-checkbox",
      type: "checkbox",
      name: "use_cron_scheduling",
      label: "Use Cron Scheduling",
      help: "Schedule this event with a Quartz cron expression",
      checked: (_d = currentConfig.use_cron_scheduling) != null ? _d : false,
      onChange: handleUseCronSchedulingChange
    }
  ), currentConfig.use_cron_scheduling ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    FormGroup,
    {
      controlId: "anomaly-cron-expression",
      validationState: validation.errors.cron_expression ? "error" : null
    },
    /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "anomaly-cron-expression",
        name: "cron_expression",
        label: "Cron Expression",
        type: "text",
        help: /* @__PURE__ */ React.createElement("span", null, cronDescription || "A Quartz cron expression to determine when the event should be run."),
        value: (_e = currentConfig.cron_expression) != null ? _e : "",
        onBlur: () => setCronDescription(describeExpression(currentConfig.cron_expression)),
        onChange: handleConfigChange
      }
    ),
    validation.errors.cron_expression && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.cron_expression[0])
  ), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Cron Time Zone"), /* @__PURE__ */ React.createElement(
    TimezoneSelect,
    {
      value: (_f = currentConfig.cron_timezone) != null ? _f : userTimezone,
      name: "cron_timezone",
      clearable: false,
      onChange: handleCronTimezoneChange
    }
  ))) : /* @__PURE__ */ React.createElement(
    FormGroup,
    {
      controlId: "anomaly-execute-every",
      validationState: validation.errors.execute_every_ms ? "error" : null
    },
    /* @__PURE__ */ React.createElement(
      TimeUnitInput,
      {
        label: "Execute search every",
        update: handleExecuteEveryChange,
        value: executeEveryMsDuration,
        unit: executeEveryMsUnit,
        units: TIME_UNITS,
        clearable: true,
        required: true
      }
    ),
    validation.errors.execute_every_ms && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.execute_every_ms[0])
  )), selectedSubtype && /* @__PURE__ */ React.createElement(FormGroup, { controlId: "anomaly-window-delay" }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: "Window Delay",
      update: handleWindowDelayChange,
      value: windowDelayMsDuration,
      unit: windowDelayMsUnit,
      units: TIME_UNITS,
      clearable: true,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, "Offsets the search window back by this amount to account for ingestion latency.")), SubtypeFormComponent && /* @__PURE__ */ React.createElement(
    SubtypeFormComponent,
    {
      eventDefinition,
      onChange,
      validation,
      section: "config"
    }
  ), selectedSubtype && !isIlluminate && /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "anomaly-schedule-checkbox",
      type: "checkbox",
      name: "_is_scheduled",
      label: "Enable",
      help: "Should this event definition be executed automatically?",
      checked: (_g = currentConfig._is_scheduled) != null ? _g : true,
      onChange: handleConfigChange
    }
  )), SubtypeFormComponent && (selectedSubtype == null ? void 0 : selectedSubtype.hasSidebar) && /* @__PURE__ */ React.createElement(Col, { md: 5, lgOffset: 1 }, /* @__PURE__ */ React.createElement(
    SubtypeFormComponent,
    {
      eventDefinition,
      onChange,
      validation,
      section: "sidebar"
    }
  )));
};
AnomalyEventForm.defaultConfig = {
  streams: [],
  filters: [],
  stream_categories: [],
  search_within_ms: 0,
  execute_every_ms: 5 * 60 * 1e3,
  window_delay_ms: 6e4,
  _is_scheduled: true,
  use_cron_scheduling: false,
  cron_expression: null,
  cron_timezone: null,
  config: null
  // No subtype selected initially
};
export default AnomalyEventForm;
